import React, { useMemo, useState, useEffect } from "react";
import { Modal, Button, FormInput, FormSelect, FormTextarea, SearchableSelect } from "@/components/ui";
import { useTelecallers } from "../hooks/useTelecallers";
import { useOfficeLocations } from "../hooks/useOfficeLocations";
import { useContactPermissions } from "../hooks/useContactPermissions";
import { INDIAN_STATES } from "@/constants/globalConstants";
import {
  REFERENCE_SOURCES,
  PROPERTY_REQUIREMENTS_ALL,
  PROPERTY_REQUIREMENTS_LEFT,
  PROPERTY_REQUIREMENTS_RIGHT
} from "../constants/contactConstants";
import { numberToWords } from "@/lib/formatters";
import { useVerveContactForm } from "../hooks/useVerveContactForm";
import { useWatch, Controller } from "react-hook-form";
import { ChevronDown, CheckCircle2 } from "lucide-react";

export const CustomerFormModal = ({
  isOpen,
  onClose,
  initialData,
  onSubmit,
  stages = [],
  mode = "add",
  currentUser = null
}) => {
  const { isAdmin: isAdminRole, isTelecaller: isTelecallerRole } = useContactPermissions(currentUser);

  const {
    form,
    control,
    onSubmit: handleFormSubmit,
    isSubmitting,
    errors,
    resetForm
  } = useVerveContactForm(initialData, onClose);

  const selectedReference = useWatch({
    control,
    name: "referenceType"
  });

  // Reset form when modal opens in "add" mode to clear previous data
  useEffect(() => {
    if (isOpen) {
      if (mode === "add") {
        resetForm();
        // Auto-populate telecaller if current user is a telecaller
        if (isTelecallerRole && currentUser?._id) {
          form.setValue("assignedTo", currentUser._id);
        }
        if (currentUser?.name) {
          form.setValue("attendedBy", currentUser.name);
        }
      }
    }
  }, [isOpen, mode, resetForm, isTelecallerRole, currentUser, form]);

  const { register, watch } = form;

  const selectedStatus = watch("status");

  const { telecallers } = useTelecallers();
  const { locations } = useOfficeLocations();


  /* Developer : Nithin
  Date : 24-04-2026
  changes:Added New Input Fields               */

  const isConverted = initialData?.isConvertedByTelecaller === true || selectedStatus === 'converted';
  const isReadOnly = !isAdminRole && (isConverted && selectedStatus !== 'drop');

  // Checkbox logic
  const propertyTypeValue = watch("propertyType") || "";

  const predefinedRequirements = PROPERTY_REQUIREMENTS_ALL;

  const requirementsList = useMemo(() => propertyTypeValue.split(',').map(s => s.trim()).filter(Boolean), [propertyTypeValue]);

  const [showOther, setShowOther] = useState(false);
  const [otherText, setOtherText] = useState("");

  useEffect(() => {
    if (isOpen) {
      const currentList = (form.getValues("propertyType") || "").split(',').map(s => s.trim()).filter(Boolean);
      const otherReqs = currentList.filter(r => !predefinedRequirements.includes(r));
      if (otherReqs.length > 0) {
        setShowOther(true);
        setOtherText(otherReqs.join(', '));
      } else {
        setShowOther(false);
        setOtherText("");
      }
    }
  }, [isOpen, form, predefinedRequirements]);

  const handleRequirementChange = (req, isChecked) => {
    let newList = [...requirementsList];
    if (isChecked) {
      if (!newList.includes(req)) newList.push(req);
    } else {
      newList = newList.filter(item => item !== req);
    }
    // Sync to form
    form.setValue("propertyType", newList.join(', '), { shouldDirty: true });
  };

  const handleOtherCheckbox = (isChecked) => {
    setShowOther(isChecked);
    let newList = requirementsList.filter(r => predefinedRequirements.includes(r));
    if (!isChecked) {
      setOtherText("");
    } else if (otherText.trim()) {
      newList.push(otherText.trim());
    }
    form.setValue("propertyType", newList.join(', '), { shouldDirty: true });
  };

  const handleOtherTextChange = (e) => {
    const val = e.target.value;
    setOtherText(val);
    const newList = requirementsList.filter(r => predefinedRequirements.includes(r));
    if (val.trim()) {
      newList.push(val.trim());
    }
    form.setValue("propertyType", newList.join(', '), { shouldDirty: true });
  };

  const watchedValues = watch();
  const isPersonalFilled = !!(watchedValues.firstName && watchedValues.phoneNumber && watchedValues.city && watchedValues.state);
  const isRequirementFilled = !!(watchedValues.propertyType && watchedValues.propertyType.length > 0);
  const isSiteVisitFilled = !!(watchedValues.meetingDate || watchedValues.alternativeMeetingDate || watchedValues.attendedBy || watchedValues.notes);

  /* Developer : Nithin
  Date : 24-04-2026
  changes: Added section wise drop down (accordion) and filled indication */
  const [expandedSections, setExpandedSections] = useState(['personal']);

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? []
        : [section]
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'edit' ? "Edit Contact" : "Add New Contact"} size="max-w-3xl">
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-3">

        {/* Section 01 */}
        <div className="border border-slate-200 rounded-md">
          <button
            type="button"
            className={`w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-left hover:bg-slate-100 transition-colors ${expandedSections.includes('personal') ? 'rounded-t-md' : 'rounded-md'}`}
            onClick={() => toggleSection('personal')}
          >
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-normal text-slate-700">01. Personal Info</span>
              {isPersonalFilled && <CheckCircle2 size={16} className="text-emerald-500" />}
            </div>
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${expandedSections.includes('personal') ? 'rotate-180' : ''}`} />
          </button>

          {expandedSections.includes('personal') && (
            <div className="p-4 bg-white border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 rounded-b-md">

              <FormInput
                label="First Name"
                required
                {...register("firstName")}
                error={errors.firstName?.message}
                readOnly={isReadOnly}
                placeholder="First name"
              />
              <FormInput
                label="Last Name"
                {...register("lastName")}
                error={errors.lastName?.message}
                readOnly={isReadOnly}
                placeholder="Last name"
              />
              <FormInput
                label="Phone Number"
                required
                type="tel"
                {...register("phoneNumber")}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                }}
                maxLength={10}
                error={errors.phoneNumber?.message}
                readOnly={isReadOnly}
                placeholder="10-digit number (no 0 or +91)"
              />
              <FormInput
                label="Email Address"
                type="email"
                {...register("email")}
                error={errors.email?.message}
                readOnly={isReadOnly}
                placeholder="Email address"
              />
              <FormInput
                label="Address Line 1"
                {...register("addressLine1")}
                error={errors.addressLine1?.message}
                readOnly={isReadOnly}
                placeholder="Street address, PO Box, etc."
              />
              <FormInput
                label="Address Line 2"
                {...register("addressLine2")}
                error={errors.addressLine2?.message}
                readOnly={isReadOnly}
                placeholder="Apartment, suite, unit, etc."
              />
              <FormInput
                label="City"
                required
                {...register("city")}
                error={errors.city?.message}
                readOnly={isReadOnly}
                placeholder="City"
              />
              <FormInput
                label="District"
                {...register("district")}
                error={errors.district?.message}
                readOnly={isReadOnly}
                placeholder="District"
              />
              <FormSelect
                label="State"
                required
                {...register("state")}
                error={errors.state?.message}
                disabled={isReadOnly}
                options={INDIAN_STATES}
                placeholder="-- Select State --"
              />
              <FormInput
                label="PIN Code"
                type="tel"
                {...register("pincode")}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                }}
                maxLength={6}
                error={errors.pincode?.message}
                readOnly={isReadOnly}
                placeholder="6-digit PIN"
              />

              <FormSelect
                label="Office Location"
                {...register("officeLocation")}
                error={errors.officeLocation?.message}
                disabled={isReadOnly}
                placeholder="All Locations"
              >
                {locations.map(loc => (
                  <option key={loc._id} value={loc.name}>{loc.name}</option>
                ))}
              </FormSelect>

              <FormSelect
                label="Assign To Telecaller"
                {...register("assignedTo")}
                error={errors.assignedTo?.message}
                disabled={isTelecallerRole || isReadOnly}
                placeholder="-- Select Telecaller --"
              >
                {telecallers.map(t => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </FormSelect>

              <Controller
                control={control}
                name="referenceType"
                render={({ field: { onChange, value } }) => (
                  <SearchableSelect
                    label="Reference Type"
                    placeholder="-- Select Reference type --"
                    value={value}
                    onChange={onChange}
                    error={errors.referenceType?.message}
                    readOnly={isReadOnly}
                    options={REFERENCE_SOURCES}
                  />
                )}
              />

              {/* <FormSelect
                label={mode === 'edit' ? "Current Status" : "Initial Status"}
                {...register("status")}
                error={errors.status?.message}
                disabled={true}
                placeholder="New Contact"
              >
                {stages.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </FormSelect> */}

              <Controller
                control={control}
                name="budget"
                render={({ field: { onChange, value } }) => (
                  <div className="flex flex-col gap-0.5 w-full">
                    <FormInput
                      label="Customer Budget (₹)"
                      type="text"
                      value={value ? Number(value).toLocaleString('en-IN') : ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        onChange(val ? Number(val) : '');
                      }}
                      error={errors.budget?.message}
                      placeholder="e.g. 2,50,000"
                      readOnly={isReadOnly}
                    />
                    {value && !isNaN(value) && Number(value) > 0 ? (
                      <span className="text-[10px] font-medium text-slate-500 capitalize px-1 mt-0.5">
                        {numberToWords(Number(value))}
                      </span>
                    ) : null}
                  </div>
                )}
              />

              {/* Developer : Nithin
              Date : 24-04-2026
              changes:    updated form                    */}
              {selectedReference === 'Others' && (
                <div className="sm:col-span-2">
                  <FormInput
                    label="Referral Name / Details"
                    {...register("referenceDetails")}
                    error={errors.referenceDetails?.message}
                    placeholder="Enter name or details of the person who referred..."
                    readOnly={isReadOnly}
                  />
                </div>
              )}

            </div>
          )}
        </div>

        {/* Section 02 */}
        <div className="border border-slate-200 rounded-md">
          <button
            type="button"
            className={`w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-left hover:bg-slate-100 transition-colors ${expandedSections.includes('requirement') ? 'rounded-t-md' : 'rounded-md'}`}
            onClick={() => toggleSection('requirement')}
          >
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-normal text-slate-700">02. Requirement</span>
              {isRequirementFilled && <CheckCircle2 size={16} className="text-emerald-500" />}
            </div>
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${expandedSections.includes('requirement') ? 'rotate-180' : ''}`} />
          </button>

          {expandedSections.includes('requirement') && (
            <div className="p-4 bg-white border-t border-slate-200 rounded-b-md">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">

                {PROPERTY_REQUIREMENTS_LEFT.map((leftReq, index) => {
                  const rightReq = PROPERTY_REQUIREMENTS_RIGHT[index];
                  return (
                    <React.Fragment key={index}>
                      <label className="flex items-center gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-brand-orange rounded border-slate-300 focus:ring-brand-orange transition-colors cursor-pointer disabled:opacity-50 shrink-0"
                          checked={requirementsList.includes(leftReq)}
                          onChange={(e) => handleRequirementChange(leftReq, e.target.checked)}
                          disabled={isReadOnly}
                        />
                        <span className="text-[13px] font-normal text-slate-500 group-hover:text-brand-dark transition-colors">{leftReq}</span>
                      </label>

                      {rightReq && (
                        <label className="flex items-center gap-2.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-brand-orange rounded border-slate-300 focus:ring-brand-orange transition-colors cursor-pointer disabled:opacity-50 shrink-0"
                            checked={requirementsList.includes(rightReq)}
                            onChange={(e) => handleRequirementChange(rightReq, e.target.checked)}
                            disabled={isReadOnly}
                          />
                          <span className="text-[13px] font-normal text-slate-500 group-hover:text-brand-dark transition-colors">{rightReq}</span>
                        </label>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Row 4 - Others spanning full width */}
                <div className="col-span-full flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-1">
                  <label className="flex items-center gap-2.5 cursor-pointer group shrink-0">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-brand-orange rounded border-slate-300 focus:ring-brand-orange transition-colors cursor-pointer disabled:opacity-50 shrink-0"
                      checked={showOther}
                      onChange={(e) => handleOtherCheckbox(e.target.checked)}
                      disabled={isReadOnly}
                    />
                    <span className="text-[13px] font-normal text-slate-500 group-hover:text-brand-dark transition-colors whitespace-nowrap">Others (please mention)</span>
                  </label>
                  {showOther && (
                    <div className="w-full sm:w-1/2 flex-1">
                      <input
                        type="text"
                        className="w-full h-[35px] text-[13px] text-slate-800 placeholder-slate-400 border border-slate-200 rounded-md px-3 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/30 transition-all bg-white"
                        value={otherText}
                        onChange={handleOtherTextChange}
                        placeholder="Please specify other requirements..."
                        readOnly={isReadOnly}
                      />
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}
        </div>

        {/* Section 03 */}
        <div className="border border-slate-200 rounded-md">
          <button
            type="button"
            className={`w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-left hover:bg-slate-100 transition-colors ${expandedSections.includes('site') ? 'rounded-t-md' : 'rounded-md'}`}
            onClick={() => toggleSection('site')}
          >
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-normal text-slate-700">03. Site Visit Details</span>
              {isSiteVisitFilled && <CheckCircle2 size={16} className="text-emerald-500" />}
            </div>
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${expandedSections.includes('site') ? 'rotate-180' : ''}`} />
          </button>

          {expandedSections.includes('site') && (
            <div className="p-4 bg-white border-t border-slate-200 rounded-b-md">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-x-4 gap-y-3">
                <div className="sm:col-span-6 flex gap-2">
                  <FormInput
                    label="Preferred Date"
                    type="date"
                    className="flex-1"
                    {...register("meetingDate")}
                    error={errors.meetingDate?.message}
                    readOnly={isReadOnly}
                  />
                  <FormInput
                    label="Time (Opt.)"
                    type="time"
                    className="w-[135px]"
                    {...register("meetingTime")}
                    error={errors.meetingTime?.message}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="sm:col-span-6 flex gap-2">
                  <FormInput
                    label="Alternative Date"
                    type="date"
                    className="flex-1"
                    {...register("alternativeMeetingDate")}
                    error={errors.alternativeMeetingDate?.message}
                    readOnly={isReadOnly}
                  />
                  <FormInput
                    label="Time (Opt.)"
                    type="time"
                    className="w-[135px]"
                    {...register("alternativeMeetingTime")}
                    error={errors.alternativeMeetingTime?.message}
                    readOnly={isReadOnly}
                  />
                </div>

                {/* Attended By is hidden from UI but data is preserved and saved */}
                <input type="hidden" {...register("attendedBy")} />
                {/* 
                <div className="sm:col-span-2">
                  <FormInput
                    label="Attended By"
                    type="text"
                    {...register("attendedBy")}
                    error={errors.attendedBy?.message}
                    readOnly={isReadOnly}
                    placeholder="Enter name"
                  />
                </div>
                */}
              </div>

              <div className="mt-3">
                <FormTextarea
                  label="Notes"
                  rows={3}
                  {...register("notes")}
                  error={errors.notes?.message}
                  placeholder="Additional details..."
                  readOnly={isReadOnly}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-4 pt-3 border-t border-slate-100 w-full">
          <Button variant="outline" className="flex-1 h-11" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="orange"
            className="flex-1 h-11"
            disabled={isSubmitting || isReadOnly}
            loading={isSubmitting}
          >
            {mode === 'edit' ? "Save Changes" : "Add Customer"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
